import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios'
import { ApiError } from './errors'
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  setTokens,
} from './session'
import type { ApiErrorBody, Envelope, PageMeta, RefreshResponse } from './types'

/** 재시도 1회 표시. `_retried` 없이 무한 재시도하면 401 루프가 된다. */
interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean
  /** 401 을 인터셉터가 처리하지 않게 한다 (로그인·리프레시 자체) */
  _skipAuthRefresh?: boolean
}

const baseURL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/api'

export const http: AxiosInstance = axios.create({
  baseURL,
  timeout: 30_000,
  headers: { Accept: 'application/json' },
})

/* ── 화면 언어 ─────────────────────────────────────────────────────
 * 서버가 알림·상태 라벨을 번역해서 보내므로 `Accept-Language` 를 반드시 실어야
 * 한다. 안 보내면 영어 화면에 한국어 알림이 섞인다(실제로 겪음).
 */
let currentLocale = 'en'

export function setApiLocale(locale: string): void {
  currentLocale = locale
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (null !== token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  config.headers['Accept-Language'] = currentLocale

  return config
})

/* ── 리프레시 합치기 ───────────────────────────────────────────────
 * 대시보드는 첫 화면에서 요청을 5~6개 동시에 던진다. 토큰이 만료돼 있으면 전부
 * 401 이 되고, 각자 리프레시를 호출하면 **서버가 재사용 감지로 세션을 끊는다**
 * (`TokenService::rotate()` 는 회전한 토큰의 재사용을 탈취로 본다).
 * 그래서 진행 중인 리프레시 프라미스를 하나만 두고 모두 그것을 기다린다.
 */
let refreshInFlight: Promise<string> | null = null

type SessionExpiredHandler = () => void
let onSessionExpired: SessionExpiredHandler = () => {}

/** 세션이 완전히 끊겼을 때 호출된다(스토어가 로그인 화면으로 보낸다). */
export function setSessionExpiredHandler(handler: SessionExpiredHandler): void {
  onSessionExpired = handler
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = getRefreshToken()
  if (null === refreshToken) {
    throw new ApiError('NO_SESSION', 'Your session has ended. Please sign in again.', 401)
  }

  // 리프레시는 `http` 인스턴스를 쓰지만 인터셉터를 타지 않게 표시한다.
  const response = await http.post<Envelope<RefreshResponse>>(
    '/auth/refresh',
    { refresh_token: refreshToken },
    { _skipAuthRefresh: true } as AxiosRequestConfig,
  )

  const tokens = response.data.data
  setTokens(tokens.access_token, tokens.refresh_token, tokens.expires_in)

  return tokens.access_token
}

function refreshOnce(): Promise<string> {
  refreshInFlight ??= refreshAccessToken().finally(() => {
    // 성공이든 실패든 비운다. 비우지 않으면 실패한 프라미스가 영구 캐시된다.
    refreshInFlight = null
  })

  return refreshInFlight
}

function toApiError(error: AxiosError): ApiError {
  const response = error.response
  if (undefined === response) {
    return ApiError.transport(
      'The server could not be reached. Check your connection and try again.',
    )
  }

  const body = response.data as Partial<ApiErrorBody> | undefined
  if (undefined !== body?.error && 'string' === typeof body.error.message) {
    return ApiError.fromBody(body as ApiErrorBody, response.status)
  }

  // 봉투가 없는 응답(프록시 502, HTML 오류 페이지). 상태 코드라도 남긴다.
  return ApiError.transport(
    `Unexpected server response (HTTP ${response.status}).`,
    response.status,
  )
}

http.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined

    const canRetry =
      401 === error.response?.status &&
      undefined !== config &&
      true !== config._retried &&
      true !== config._skipAuthRefresh &&
      null !== getRefreshToken()

    if (canRetry && undefined !== config) {
      config._retried = true
      try {
        const token = await refreshOnce()
        config.headers.Authorization = `Bearer ${token}`

        return await http.request(config)
      } catch (refreshError) {
        clearTokens()
        onSessionExpired()

        // 리프레시 실패 사유(재사용 감지 등)를 그대로 올린다. 여기서 원래 401 로
        // 바꿔치면 "왜 로그아웃됐는지" 를 사용자에게 말할 수 없다.
        throw refreshError instanceof ApiError ? refreshError : toApiError(error)
      }
    }

    if (401 === error.response?.status && true !== config?._skipAuthRefresh) {
      clearTokens()
      onSessionExpired()
    }

    throw toApiError(error)
  },
)

/* ── 얇은 헬퍼 ─────────────────────────────────────────────────────
 * 컨트롤러 응답은 항상 `{ data, meta? }` 다. 화면마다 `.data.data` 를 쓰면
 * 봉투가 바뀔 때 전부 고쳐야 하므로 여기서 한 번만 벗긴다.
 */

export async function getData<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await http.get<Envelope<T>>(url, config)

  return response.data.data
}

export interface Paged<T> {
  items: T[]
  meta: PageMeta
}

export async function getPaged<T>(
  url: string,
  config?: AxiosRequestConfig,
): Promise<Paged<T>> {
  const response = await http.get<Envelope<T[]>>(url, config)
  const meta = response.data.meta

  return {
    items: response.data.data,
    meta: meta ?? { page: 1, perPage: response.data.data.length, total: response.data.data.length, totalPages: 1 },
  }
}

export async function postData<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await http.post<Envelope<T>>(url, body, config)

  return response.data.data
}

export async function patchData<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.patch<Envelope<T>>(url, body)

  return response.data.data
}

export async function putData<T>(url: string, body?: unknown): Promise<T> {
  const response = await http.put<Envelope<T>>(url, body)

  return response.data.data
}

/**
 * DELETE. 본문이 필요한 경우가 있다(관리자 삭제 사유). axios 는 `data` 옵션으로
 * 실어 보낸다 — 두 번째 인자로 넘기면 조용히 무시된다.
 */
export async function deleteData(url: string, body?: unknown): Promise<void> {
  await http.delete(url, undefined === body ? undefined : { data: body })
}

/** 리프레시를 강제로 태우는 경로(부팅 시 액세스 토큰이 없을 때). */
export { refreshOnce }
